import { Panel, PageHeader } from "@/components/admin-shell";
import { Plus, Download, Upload, Edit, Trash2, AlertCircle } from "lucide-react";
import { IProduct } from "./ProductTypes";

interface ProductTableProps {
  products: IProduct[];
  isLoading: boolean;
  error: unknown;
  onAddClick: () => void;
  onEditClick: (product: IProduct) => void;
  onDeleteClick: (id: string, name: string) => void;
  onExportCSV: () => void;
  onImportCSV: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function ProductTable({
  products,
  isLoading,
  error,
  onAddClick,
  onEditClick,
  onDeleteClick,
  onExportCSV,
  onImportCSV,
}: ProductTableProps) {
  const fmt = (n: number) => `৳${n.toLocaleString("en-BD")}`;

  return (
    <>
      <PageHeader
        title="Products & Inventory"
        subtitle="Manage your fragrance catalogue, variants, stock and visibility."
        action={
          <div className="flex flex-wrap gap-2">
            <button
              onClick={onExportCSV}
              className="inline-flex items-center gap-2 rounded-md border border-gold/30 px-3.5 py-2 text-sm font-semibold text-gold hover:bg-accent/40 transition cursor-pointer"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <label className="inline-flex items-center gap-2 rounded-md border border-gold/30 px-3.5 py-2 text-sm font-semibold text-gold hover:bg-accent/40 transition cursor-pointer">
              <Upload className="h-4 w-4" /> Import CSV
              <input type="file" accept=".csv" className="hidden" onChange={onImportCSV} />
            </label>
            <button
              onClick={onAddClick}
              className="inline-flex items-center gap-2 rounded-md bg-gold px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 transition cursor-pointer"
            >
              <Plus className="h-4 w-4" /> Add product
            </button>
          </div>
        }
      />

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gold border-t-transparent" />
          <span className="ml-3">Loading catalog...</span>
        </div>
      ) : error ? (
        <div className="flex h-64 flex-col items-center justify-center text-center p-6 border border-gold/15 bg-card/30 rounded-xl">
          <AlertCircle className="h-10 w-10 text-destructive mb-3" />
          <h3 className="text-lg font-bold text-foreground">Failed to load catalog</h3>
          <p className="text-sm text-muted-foreground mt-1">Check backend connection.</p>
        </div>
      ) : (
        <Panel className="p-0 overflow-hidden border border-gold/20 bg-card/30">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse">
              <thead className="border-b border-gold/15 bg-accent/25">
                <tr className="text-xs font-bold uppercase tracking-wider text-gold-soft">
                  <th className="px-6 py-4">Product</th>
                  <th className="px-6 py-4">Category</th>
                  <th className="px-6 py-4 text-right">Price Matrix</th>
                  <th className="px-6 py-4 text-right">Inventory Count</th>
                  <th className="px-6 py-4">Status & Badges</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gold/10">
                {products.map((p) => {
                  const totalStock = (p.sizes || []).reduce((sum, s) => sum + s.stock, 0);
                  const isAnyLow = (p.sizes || []).some(
                    (s) => s.stock < (p.lowStockThreshold || 5),
                  );

                  return (
                    <tr key={p._id} className="hover:bg-accent/15 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={
                              p.images?.[0] ||
                              "https://res.cloudinary.com/demo/image/upload/v1672322312/product-oud-royale.jpg"
                            }
                            alt={p.name.en}
                            className="h-12 w-12 rounded-md border border-gold/20 object-cover"
                          />
                          <div>
                            <div className="font-semibold text-foreground text-base leading-tight">
                              {p.name.en}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5" lang="bn">
                              {p.name.bn}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-muted-foreground capitalize">{p.category}</td>
                      <td className="px-6 py-4 text-right">
                        {p.sizes && p.sizes.length > 0 ? (
                          <div className="space-y-0.5">
                            {p.sizes.map((s, idx) => (
                              <div key={idx} className="text-xs text-foreground/90">
                                <span className="text-muted-foreground font-mono">{s.ml}ml:</span>{" "}
                                {fmt(s.salePrice ?? s.price)}
                                {s.costPrice ? (
                                  <span className="text-[10px] text-amber-400 ml-1 font-mono" title="Manufacturing Cost">
                                    (Cost: {fmt(s.costPrice)})
                                  </span>
                                ) : p.costPrice ? (
                                  <span className="text-[10px] text-amber-400 ml-1 font-mono" title="Base Product Cost">
                                    (Cost: {fmt(p.costPrice)})
                                  </span>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">No variants</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex flex-col items-end">
                          <span
                            className={`font-semibold ${
                              totalStock === 0
                                ? "text-red-400"
                                : isAnyLow
                                  ? "text-amber-400 animate-pulse"
                                  : "text-emerald-400"
                            }`}
                          >
                            {totalStock === 0 ? "Out of stock" : `${totalStock} pcs`}
                          </span>
                          <span className="text-[10px] text-muted-foreground mt-0.5">
                            ({p.sizes?.length || 0} sizes)
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1.5 items-center">
                          <span
                            className={`inline-flex rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                              p.isActive
                                ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                                : "bg-rose-500/10 text-rose-400 border-rose-500/30"
                            }`}
                          >
                            {p.isActive ? "Visible" : "Hidden"}
                          </span>
                          {(p.badges || []).map((b) => (
                            <span
                              key={b}
                              className="inline-flex rounded-full bg-gold/10 border border-gold/30 text-gold text-[9px] font-semibold px-2 py-0.5 uppercase"
                            >
                              {b.replace("-", " ")}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => onEditClick(p)}
                            className="rounded border border-gold/20 p-1.5 text-gold-soft hover:bg-accent/40 hover:text-gold transition cursor-pointer"
                            title="Edit product info"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => onDeleteClick(p._id, p.name.en)}
                            className="rounded border border-gold/20 p-1.5 text-gold-soft hover:bg-rose-500/10 hover:text-rose-400 transition cursor-pointer"
                            title="Delete product"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </>
  );
}
